import React from 'react';
import { cn } from '@/lib/utils';
import { CheckIcon, Clock, CircleIcon, AlertCircle } from 'lucide-react';

type StatusType = 'not started' | 'in progress' | 'ready' | 'on hold';

interface StatusStep {
  value: StatusType;
  label: string;
}

interface StatusIndicatorProps {
  currentStatus: StatusType;
  className?: string;
}

const statusSteps: StatusStep[] = [
  { value: 'not started', label: 'Not Started' },
  { value: 'in progress', label: 'In Progress' },
  { value: 'ready', label: 'Ready' }
];

// Define the On Hold step separately
const ON_HOLD_STEP: StatusStep = {
  value: 'on hold',
  label: 'On Hold'
};

const getStatusIcon = (status: StatusType, isCompleted: boolean, isCurrent: boolean) => {
  // Always use a check mark for completed steps
  if (isCompleted) {
    return <CheckIcon className="h-4 w-4" />;
  }
  
  // For the current step, use the appropriate icon based on status
  if (isCurrent) {
    if (status === 'not started') {
      return <CircleIcon className="h-4 w-4" />;
    }
    if (status === 'in progress') {
      return <Clock className="h-4 w-4" />;
    }
    if (status === 'ready') {
      return <CheckIcon className="h-4 w-4" />;
    }
    if (status === 'on hold') {
      return <AlertCircle className="h-4 w-4" />;
    }
  }
  
  // For upcoming steps, use empty circle
  return <CircleIcon className="h-4 w-4" />;
};

export function StatusIndicator({ currentStatus, className }: StatusIndicatorProps) {
  // Create the steps array based on current status
  let steps = [...statusSteps];
  
  // Only add the 'on hold' step if it's the current status
  if (currentStatus === 'on hold') {
    steps = [ON_HOLD_STEP, ...steps];
  }
  
  // Find the index of the current status in our dynamic steps array
  const currentIndex = steps.findIndex(step => step.value === currentStatus);
  
  return (
    <div className={cn("w-full", className)}>
      <div className="relative">
        {/* Icons and connector lines in a separate row for proper alignment */}
        <div className="flex items-center justify-center mb-2">
          {steps.map((step, index) => {
            // Determine if this step is completed, current, or upcoming
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            
            // Active color based on current status
            let activeColor;
            if (currentStatus === 'not started') {
              activeColor = "#64748b"; // Slate gray for not started
            } else if (currentStatus === 'in progress') {
              activeColor = "#f59e0b"; // Yellow/amber for in progress
            } else if (currentStatus === 'on hold') {
              activeColor = "#ef4444"; // Red for on hold
            } else if (currentStatus === 'ready') {
              activeColor = "#10b981"; // Green for completed
            } else {
              activeColor = "#64748b"; // Default to gray
            }
            
            // Determine icon color class
            let iconColorClass = "text-gray-300"; // Default for upcoming steps
            
            if (isCurrent) {
              // Current step has white icon
              iconColorClass = "text-white";
            } else if (isCompleted) {
              // Completed steps use the active color
              iconColorClass = `text-[${activeColor}]`;
            }
            
            return (
              <React.Fragment key={`icon-${step.value}`}>
                {/* Step icon with colored background for current step */}
                <div 
                  className={cn(
                    "flex items-center justify-center rounded-full p-1 z-10 w-8 h-8", // Fixed width/height for consistent sizing
                    isCurrent && `bg-[${activeColor}]`, // Current step has active color background
                    !isCurrent && "bg-white" // Non-current steps have white background
                  )}
                >
                  <div className={iconColorClass}>
                    {getStatusIcon(step.value, isCompleted, isCurrent)}
                  </div>
                </div>
                
                {/* Connector line between steps */}
                {index < steps.length - 1 && (
                  <div className={cn(
                    "flex-1 h-0.5 mx-2", // Slightly more spacing
                    index < currentIndex ? `bg-[${activeColor}]` : "bg-gray-200" // Completed lines use active color
                  )} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Labels in a separate row */}
        <div className="flex px-1">
          {steps.map((step, index) => {
            // Determine if this step is completed, current, or upcoming
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isUpcoming = index > currentIndex;
            
            // Get active color (same logic as above)
            let activeColor;
            if (currentStatus === 'not started') {
              activeColor = "#64748b";
            } else if (currentStatus === 'in progress') {
              activeColor = "#f59e0b";
            } else if (currentStatus === 'on hold') {
              activeColor = "#ef4444";
            } else if (currentStatus === 'ready') {
              activeColor = "#10b981";
            } else {
              activeColor = "#64748b";
            }
            
            // All labels should have equal width for proper centering
            const width = 'flex-1 text-center';
            
            return (
              <div 
                key={`label-${step.value}`}
                className={cn(
                  width,
                  index === 0 && 'text-left pl-1', // Add a bit of padding for first item
                  index === steps.length - 1 && 'text-right pr-1', // Add a bit of padding for last item
                )}
              >
                <p 
                  className={cn(
                    "text-xs font-medium",
                    isCompleted && `text-[${activeColor}]`, // Completed steps text uses active color
                    isCurrent && `text-[${activeColor}]`, // Current step text uses active color
                    isUpcoming && "text-gray-400" // Upcoming steps text is light gray
                  )}
                >
                  {step.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
